import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { useApi } from "@/api/hooks/useApi";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { CommunityMessage } from "@/components/CommunityFeed/CommunityMessage";
import { getCommunityPostDocumentTitle } from "@/components/CommunityFeed/getCommunityPostDocumentTitle";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Redirect } from "@/components/Routing/Redirect";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { isHttpError } from "@/helpers/Network/errors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function MessageDetailPage(): React.ReactNode {
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();

  const api = useApi();
  const { id: postId } = useParams<{ id: string }>();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: postDetails,
    isPending: isPendingPostDetails,
    error: errorPostDetails,
  } = useQuery({
    ...communityFeedQueries.messageDetails(postId!),
    enabled: !!postId,
  });

  useEffect(() => {
    if (postDetails?.isUnread && postId) {
      void api.postMessagesReadV1(postId);
    }
  }, [api, postDetails?.isUnread, postId]);

  // Optimistically update UI
  if (postDetails?.isUnread) {
    // oxlint-disable-next-line react-hooks/immutability
    postDetails.isUnread = false;
  }

  const redirectState: MessageRouteStates["list"] = useMemo(() => ({ contentNotFoundReason: "not-found" }), []);

  // If post is not found, redirect to message feed list with notification
  if (isHttpError(errorPostDetails) && errorPostDetails.status === 404) {
    return <Redirect to={routes.communityFeed.list} state={redirectState} />;
  }

  if (errorPostDetails) {
    return <ErrorPage error={errorPostDetails} />;
  }

  if (isPendingPostDetails) {
    return <FullSizeLoader />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={getCommunityPostDocumentTitle(postDetails, t("page.message-details.title"))}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.message-feed.title"),
              to: routes.communityFeed.list({
                slug,
              }),
            },
            {
              name: t("page.message-details.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [projectId] }}
          context={{ scope: "project", entity: "message", page: "detail", entityId: postDetails.id }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.post-detail.suggestion.1"),
            t("component.ask-nobi-chat-drawer.post-detail.suggestion.2"),
          ]}
        />
      }
      contentSize="constrained"
    >
      <CommunityPostViewCountTrackingProvider>
        <CommunityMessage message={postDetails} isExpanded />
      </CommunityPostViewCountTrackingProvider>
    </DocumentPage>
  );
}
